import api from './api';

const unwrap = value => value?.Data ?? value?.data ?? value;
const rowsOf = value => {
  const data = unwrap(value);
  return Array.isArray(data) ? data : data?.Objects ?? [];
};
const isIdentifier = value =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(value ?? '')
  );
const safeLabel = value =>
  typeof value === 'string' && value.trim() && !isIdentifier(value)
    ? value.trim()
    : '';

export const billingActivityLabel = value => {
  const labels = {
    HostedDataDeleted: 'Hosted data deleted',
    HostedDataPurged: 'Hosted data deleted',
    CreateHostedAccount: 'Hosted account created',
    HostedAccountState: 'Hosted account',
    OrganizationPayment: 'Payment'
  };
  if (labels[value]) return labels[value];
  const text = safeLabel(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .trim();
  return text
    ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
    : 'Billing activity';
};

export const billingAuditKey = event =>
  `${event.EntityType}:${event.EntityGUID}`;

// Only resolve references present on this audit page. All reads use existing,
// authenticated tenant APIs; IDs are lookup keys and never display labels.
export const loadBillingAuditLabels = async events => {
  const users = {};
  const entities = {};
  const resolvePages = async (ids, load, labels, labelOf) => {
    const remaining = new Set(ids.filter(Boolean));
    let offset = 0;
    while (remaining.size) {
      const response = await load(offset, 100);
      const rows = rowsOf(response);
      for (const row of rows) {
        if (!remaining.has(row.GUID)) continue;
        const label = safeLabel(labelOf(row));
        if (label) labels[row.GUID] = label;
        remaining.delete(row.GUID);
      }
      const data = unwrap(response);
      if (!rows.length || Array.isArray(data) || data?.EndOfResults === true)
        break;
      offset += rows.length;
      if (data?.TotalRecords != null && offset >= data.TotalRecords) break;
    }
  };
  const tasks = [
    resolvePages(
      events.map(event => event.ActorUserMasterGUID),
      (indexstart, maxresults) =>
        api.TenantUsers.listPaged({ indexstart, maxresults }),
      users,
      user =>
        user.FullName ||
        [user.FirstName, user.LastName].filter(Boolean).join(' ') ||
        user.Email
    )
  ];
  const sources = [
    [
      'Sponsorship',
      (offset, size) => api.OrganizationBilling.sponsorships(offset, size),
      row => row.NotaryName
    ],
    [
      'HostedAccount',
      (offset, size) => api.OrganizationBilling.hostedAccounts(offset, size),
      row => row.NotaryName
    ],
    [
      'Invoice',
      (offset, size) => api.OrganizationBilling.invoices(offset, size),
      row => row.InvoiceNumber
    ],
    [
      'PaymentMethod',
      () => api.OrganizationBilling.paymentMethods(),
      row =>
        row.Last4
          ? `${row.CardType || 'Card'} ending in ${row.Last4}`
          : 'Payment card'
    ]
  ];
  for (const [type, load, labelOf] of sources) {
    const ids = events
      .filter(event => event.EntityType === type)
      .map(event => event.EntityGUID);
    if (!ids.length) continue;
    tasks.push(
      (async () => {
        const labels = {};
        await resolvePages(ids, load, labels, labelOf);
        for (const [id, label] of Object.entries(labels))
          entities[`${type}:${id}`] = label;
      })()
    );
  }
  // Missing/deleted references and a denied lookup must not hide audit history.
  await Promise.allSettled(tasks);
  return { users, entities };
};

export const billingAuditActor = (event, labels) => {
  if (event.ActorUserMasterGUID)
    return (
      labels?.users?.[event.ActorUserMasterGUID] ||
      'Organization user (name unavailable)'
    );
  const services = {
    OrganizationBilling: 'Billing service',
    Collection: 'Automatic payment collection',
    OrganizationBillingCollection: 'Automatic payment collection',
    OrganizationBillingCycle: 'Billing scheduler',
    OrganizationHostedBilling: 'Automatic hosting update',
    OrganizationSponsorshipBoundary: 'Automatic sponsorship update',
    OrganizationSponsorshipCharge: 'Automatic subscription payment'
  };
  return (
    services[event.ActorService] ||
    (safeLabel(event.ActorService)
      ? billingActivityLabel(event.ActorService)
      : 'System')
  );
};
